import { Bound } from "lib/@components/binding/Bound"
import { isEnabled } from "lib/@components/feature"
import { FEATURE_FLAG_MOBILISER_ADVANCED_MAPPING } from "routes/asset-register/plugins/asset-mapping/feature-flag-mobiliser-advanced-mapping.runtime"
import {
    Box,
    Button,
    CircularProgress,
    Divider,
    FormControlLabel,
    Grid2 as Grid,
    IconButton,
    List,
    Radio,
    Stack,
    TableCell,
    TableRow,
    Tooltip,
    Typography,
} from "@mui/material"
import { Repeat } from "lib/@components/repeat"
import { AvailableSchedule } from "routes/asset-register/plugins/asset-mapping/available-schedule"
import Iconify from "minimals-template/components/Iconify"
import { useEffect, useRef, useState } from "react"
import { ListItemBox } from "lib/@components/ListItemBox"
import { setChosenSchedule } from "routes/asset-register/plugins/asset-mapping/controller/set-chosen-schedule"
import { useBoundContext } from "lib/@components/binding/use-bound-context"
import { useTreeItem } from "routes/schedule/lib/useTreeItem"
import { MappingUpdated } from "routes/asset-register/plugins/asset-mapping/asset-mapping.runtime"
import { ScheduleOptions } from "routes/asset-register/plugins/schedule-options/schedule-options"
import { If } from "lib/@components/switch"
import { usePreviewSchedule } from "routes/schedule/preview-schedule"
import noop from "lib/noop"
import { Question } from "routes/asset-register/plugins/asset-mapping/question"
import { rescanMapping } from "routes/asset-register/plugins/asset-mapping/controller/rescan-mapping"
import { useDisabledUntilInvalidation } from "lib/@hooks/use-disabled-until-invalidation"
import { typeFromRegister } from "routes/asset-register/plugins/asset-mapping/controller/type-from-register"
import { emptyObject } from "lib/empty-object"
import { useIsBeingMapped } from "routes/asset-register/plugins/asset-mapping/use-is-being-mapped"
import { MappingStatus } from "routes/asset-register/plugins/asset-mapping/mapping-status"
import { emptyArray } from "routes/asset-register/plugins/asset-mapping/empty-array"
import { AssetTypeStatus } from "routes/asset-register/plugins/asset-mapping/asset-type-status"
import { useAssetViewer } from "routes/asset-register/plugins/asset-mapping/use-asset-viewer"
import { useCaptureSuggestionSnapshot } from "routes/asset-register/plugins/asset-mapping/plugins/ai-mapping-feedback/use-capture-suggestion-snapshot"
import { recordSelection } from "routes/asset-register/plugins/asset-mapping/plugins/ai-mapping-feedback/record-selection"
import { confirm } from "lib/@dialogs/confirm"
import { toDataCy } from "lib/to-data-cy"
import { setManuallyChosenSchedules } from "routes/asset-register/plugins/asset-mapping/controller/set-manually-chosen-schedules.js"
import { selectItems } from "routes/schedule/lib/useSelection"
import { ManuallyMapContent } from "routes/asset-register/plugins/asset-mapping/manually-map-content.js"
import { useDialog } from "lib/@hooks/useDialog"
import { AuthoringDeleteDialog } from "authoring/routes/project/plugins/project-item/authoring-delete-dialog"
import { errorSnackbar } from "lib/snackbar/error-snackbar"
import { successSnackbar } from "lib/snackbar/success-snackbar"
import { getTreeIndex } from "routes/schedule/lib/getTreeIndex"
import { MULTI_SELECTION_REFERENCE_TYPE } from "routes/schedule/my-schedules/my-custom-schedules/constants/multi-selection-reference-type"
import { useSelectMultiSchedules } from "routes/schedule/my-schedules/my-custom-schedules/use-select-multi-schedules.js"

export function MapAssetTypeToLibrary({ item: typeName, hideDivider, onMapped = noop, onChosen = noop }) {
    const { register, isManualMapTab } = useBoundContext()
    const item = typeFromRegister.useResults(register.id, typeName) ?? emptyObject
    const [open, setOpen] = useState(false)
    const [more, setMore] = useState(false)
    const dirty = useRef(false)
    const removeQueueRef = useRef(Promise.resolve())
    useEffect(() => {
        dirty.current = false
    }, [item?.type, item?.name])
    const refresh = MappingUpdated.useRefresh(() => {
        dirty.current = true
        onMapped()
    })

    const [disabled, disable] = useDisabledUntilInvalidation("typeFromRegister")
    const isMapping = useIsBeingMapped(typeName)

    const schedulesFromDialog = useSelectMultiSchedules({
        message: `Choose schedules for "${typeName}"`,
        selectionReferenceType: MULTI_SELECTION_REFERENCE_TYPE,
    })
    const baseSchedules = item?.schedules ?? emptyArray
    const questions = item?.questions ?? emptyArray

    const custom = useTreeItem(item?.schedule_id)

    const viewAssets = useAssetViewer({
        registers: [register?._id],
        types: [item.name],
    })

    let schedules = baseSchedules
        .sortBy((c) => +c?.relevance, true)
        .filter(
            (c, i) =>
                (more && +c.relevance > 1) ||
                (+c.relevance >= 55 && i < 5) ||
                (+c.relevance >= 70 && i < 10) ||
                c.chosen ||
                c.previously_mapped
        )
    if (schedules.length === 0) schedules = baseSchedules
    useCaptureSuggestionSnapshot(register, item, schedules, open)

    const { mapping } = item
    const showMore = baseSchedules.length !== schedules.length || more
    const chosenSchedule = baseSchedules.find((c) => c.chosen)
    const manuallySelectedSchedulesLegacy = custom ? item?.schedule_id : null
    const manuallySelectedSchedules = Array.isArray(item?.schedule_ids_manual) ? item.schedule_ids_manual : emptyArray
    const manuallySelectedSchedulesCombined = Array.from(
        new Set([...manuallySelectedSchedules, manuallySelectedSchedulesLegacy].filter(Boolean))
    )
    const firstManuallySelectedScheduleId = manuallySelectedSchedulesCombined[0]
    const previewScheduleId =
        item.doNotMap === "create" ? firstManuallySelectedScheduleId ?? item?.schedule_id : chosenSchedule?.schedule_id

    const preview = usePreviewSchedule(previewScheduleId, {
        fullScreen: false,
        boost: false,
        sx: {
            my: 2,
            height: 1,
            flex: 1,
            alignSelf: "stretch",
            justifyContent: "stretch",
        },
    })
    const treeItem = useTreeItem(chosenSchedule?.schedule_id + ":sfg20/schedule")

    const intelligentMappingSelected = item.doNotMap !== "nothing" && item.doNotMap !== "create"
    const intelligentMapping = intelligentMappingSelected && !isManualMapTab
    const manualMapping = item.doNotMap === "create"
    const hasAvailableSchedules = baseSchedules.length > 0
    const showIntelligentMappingOption = !isManualMapTab || hasAvailableSchedules
    const showIntelligentDetails = isManualMapTab
        ? intelligentMappingSelected && hasAvailableSchedules
        : intelligentMappingSelected && (mapping || questions.length > 0 || hasAvailableSchedules)
    const manuallySelectedCount = manuallySelectedSchedulesCombined.length
    const isMapped = baseSchedules.some((s) => s.chosen) || (item.doNotMap === "create" && manuallySelectedCount > 0)
    const deleteAllSelectionsDialog = useDialog(
        <AuthoringDeleteDialog
            value={manuallySelectedCount > 0}
            title="Clear all selections"
            message={`Are you sure you want to clear all manually selected schedules for "${item?.name}"?`}
            messageSx={{ whiteSpace: "pre-line" }}
        />
    )

    const upgradesEnabled = isEnabled(FEATURE_FLAG_MOBILISER_ADVANCED_MAPPING)
    const displayName = getDisplayName()
    const toggleAction = getToggleAction()

    function getDisplayName() {
        if (!upgradesEnabled) return item.name
        const isRejected = item.reviewStatus === "rejected"
        const type = isRejected ? item.originalType || item.name : item.reviewedTypeName || item.name
        const parent = isRejected ? null : item.reviewedParent
        if (!parent) return type
        if (parent.toLowerCase() === type.toLowerCase()) return type
        return `${parent} / ${type}`
    }

    return (
        !Object.isEmpty(item) && (
            <Bound
                assetType={item}
                firstManuallySelectedScheduleId={firstManuallySelectedScheduleId}
                manuallySelectedCount={manuallySelectedCount}
                manuallySelectedSchedulesCombined={manuallySelectedSchedulesCombined}
                chooseSchedules={chooseSchedules}
                chosenSchedule={chosenSchedule}
                onExpandAssetRow={expandDetailsRow}
                refresh={refresh}
                removeManuallySelectedSchedule={removeManuallySelectedSchedule}
            >
                <TableRow
                    data-cy="asset-register-map-types-mappings-row"
                    sx={{
                        "& td": {
                            borderBottom: open ? "none !important" : undefined,
                        },
                    }}
                >
                    <TableCell
                        sx={{
                            width: 16,
                            p: 0,
                            verticalAlign: "middle",
                        }}
                    >
                        <IconButton
                            size="small"
                            onClick={toggle}
                            aria-label={`${toggleAction.actions} the mapping options for ${displayName}`}
                            aria-expanded={!!open}
                            data-cy={toggleAction.dataCy}
                        >
                            <Iconify icon={toggleAction.icon} />
                        </IconButton>
                    </TableCell>
                    <TableCell sx={{ width: 300, verticalAlign: "middle" }}>
                        <Typography
                            variant="body2"
                            data-cy={toDataCy("asset-register-map-types-mappings-name", displayName)}
                        >
                            {displayName}
                        </Typography>
                    </TableCell>
                    <TableCell align="right">
                        <Stack direction="row" spacing={1} justifyContent="flex-end" alignItems="center">
                            <Box>{item.count}</Box>
                            <Tooltip title="View the assets of this type" arrow>
                                <IconButton
                                    size="small"
                                    onClick={viewAssets}
                                    color="primary"
                                    aria-label={`View the ${displayName} assets`}
                                >
                                    <Iconify icon="mdi:eye" />
                                </IconButton>
                            </Tooltip>
                        </Stack>
                    </TableCell>
                    <TableCell>
                        <MappingStatus
                            baseSchedules={baseSchedules}
                            item={item}
                            onClick={preview}
                            treeItem={treeItem}
                        />
                    </TableCell>
                    <TableCell align="right">
                        <AssetTypeStatus
                            item={item}
                            isMapped={isMapped}
                            isMapping={isMapping}
                            isIntelligentMapped={intelligentMapping}
                        />
                    </TableCell>
                </TableRow>
                {!!open && (
                    <TableRow
                        data-cy="asset-register-map-types-mappings-row-detail"
                        sx={isMapping ? { opacity: 0.5, pointerEvents: "none" } : {}}
                    >
                        <TableCell sx={{ width: 28, verticalAlign: "top", pt: 6 }}></TableCell>
                        <TableCell
                            colSpan={4}
                            sx={{ width: "15vw", minWidth: 120, verticalAlign: "top", pt: 5 }}
                            data-cy="asset-item-info"
                        >
                            <Typography
                                color="text.secondary"
                                variant="body2"
                                gutterBottom
                                data-cy="asset-register-map-asset-item-type"
                            >
                                {item.type.split("|").at(-1).split(".").slice(1, 10000).join(".")}
                            </Typography>
                            <List dense>
                                <Stack sx={{ width: 1 }}>
                                    <ListItemBox alignItems="flex-start">
                                        <If truthy={showIntelligentMappingOption}>
                                            <FormControlLabel
                                                sx={{ mr: 4, whiteSpace: "nowrap" }}
                                                control={
                                                    <Radio
                                                        disabled={isManualMapTab}
                                                        onClick={intelligent}
                                                        checked={intelligentMappingSelected}
                                                        data-cy="mapping-wizard-intelligent-mapping-radio"
                                                    />
                                                }
                                                label={
                                                    <Stack direction="row" spacing={1} alignItems="center">
                                                        Intelligent Mapping
                                                    </Stack>
                                                }
                                            />
                                        </If>
                                        <Stack direction="column" spacing={1} sx={{ width: 1, alignItems: "stretch" }}>
                                            <Stack direction="row" sx={{ width: 1, alignItems: "center" }}>
                                                <Stack spacing={1}>
                                                    <FormControlLabel
                                                        sx={{ mr: 2, whiteSpace: "nowrap" }}
                                                        control={
                                                            <Radio
                                                                onClick={create}
                                                                checked={manualMapping}
                                                                data-cy="mapping-wizard-manual-mapping-radio"
                                                            />
                                                        }
                                                        label="Manually Map"
                                                    />
                                                </Stack>
                                                <FormControlLabel
                                                    sx={{ mr: 4, ml: 2, whiteSpace: "nowrap" }}
                                                    control={
                                                        <Radio
                                                            onClick={change}
                                                            checked={item.doNotMap === "nothing"}
                                                            data-cy="mapping-wizard-do-not-map-radio"
                                                        />
                                                    }
                                                    label={
                                                        <Stack direction="row" spacing={1} alignItems="center">
                                                            Exclude
                                                        </Stack>
                                                    }
                                                />
                                                <Box flex={1} />
                                                <If truthy={!isManualMapTab}>
                                                    <Button
                                                        disabled={disabled || isMapping}
                                                        size="small"
                                                        onClick={rescan}
                                                        startIcon={<Iconify icon="mdi:refresh" />}
                                                        data-cy="rescan-mappings-button"
                                                    >
                                                        Refresh
                                                    </Button>
                                                </If>
                                            </Stack>
                                            <If
                                                truthy={
                                                    isManualMapTab &&
                                                    (item.doNotMap === "create" || item.doNotMap !== "nothing")
                                                }
                                            >
                                                <ManuallyMapContent reset={reset} />
                                            </If>
                                        </Stack>
                                    </ListItemBox>
                                    <If truthy={!isManualMapTab && item.doNotMap === "create"}>
                                        <ManuallyMapContent reset={reset} />
                                    </If>
                                    <If truthy={!!item.schedule_id && item.doNotMap === "create"}>
                                        <ScheduleOptions
                                            item={{ chosen: true }}
                                            scheduleId={item.schedule_id}
                                            treeItem={{ notLicensed: false }}
                                        />
                                    </If>
                                </Stack>
                                {showIntelligentDetails && (
                                    <>
                                        <Divider sx={{ my: 1 }} />
                                        {mapping ? (
                                            <ListItemBox spacing={1} sx={{ ml: 2, mt: 1 }}>
                                                <CircularProgress color="primary" size={18} />
                                                <Typography variant="body2" color="text.secondary">
                                                    Using AI to create mapping options
                                                </Typography>
                                            </ListItemBox>
                                        ) : baseSchedules === emptyArray ? (
                                            <CircularProgress sx={{ m: 3 }} size={32} color="primary" />
                                        ) : (
                                            <>
                                                <Grid container spacing={0}>
                                                    {questions.length > -1 && (
                                                        <Grid
                                                            size={{
                                                                xs: 12,
                                                                // lg: 6,
                                                            }}
                                                            sx={
                                                                {
                                                                    // pr: 2,
                                                                    // borderRight: "1px dotted " +
                                                                    // theme.palette.action.focus,
                                                                }
                                                            }
                                                        >
                                                            <Stack
                                                                sx={{
                                                                    mt: 2,
                                                                    pb: 2,
                                                                    flex: 1,
                                                                    ...(isManualMapTab && {
                                                                        opacity: 0.6,
                                                                        filter: "grayscale(0.2)",
                                                                    }),
                                                                }}
                                                            >
                                                                <Repeat
                                                                    keyFn="question"
                                                                    list={questions}
                                                                    item={<Question />}
                                                                />
                                                            </Stack>
                                                        </Grid>
                                                    )}
                                                    <Grid size={{ xs: 12 }}>
                                                        <Repeat
                                                            keyFn="schedule_id"
                                                            list={schedules
                                                                .sortBy((s) => +s.relevance, true)
                                                                .sortBy((c) => c?.previously_mapped, true)}
                                                            item={<AvailableSchedule />}
                                                            fallback={
                                                                <Typography
                                                                    variant="body2"
                                                                    color="text.secondary"
                                                                    component="div"
                                                                    sx={{ mt: 1, ml: 2 }}
                                                                >
                                                                    Unable to detect strong matches
                                                                </Typography>
                                                            }
                                                        />

                                                        {!hideDivider && false && <Divider sx={{ my: 1 }} />}
                                                        {showIntelligentDetails && !mapping && showMore && (
                                                            <>
                                                                {more ? (
                                                                    <Button
                                                                        sx={{ ml: 0.5 }}
                                                                        onClick={() => setMore(false)}
                                                                        data-cy="less-schedules"
                                                                    >
                                                                        (less)
                                                                    </Button>
                                                                ) : (
                                                                    <Button
                                                                        sx={{ ml: 0.5 }}
                                                                        onClick={() => setMore(true)}
                                                                        data-cy="more-schedules"
                                                                    >
                                                                        more...
                                                                    </Button>
                                                                )}
                                                            </>
                                                        )}
                                                    </Grid>
                                                </Grid>
                                            </>
                                        )}
                                    </>
                                )}
                            </List>
                        </TableCell>
                    </TableRow>
                )}
            </Bound>
        )
    )

    async function rescan() {
        disable()
        await rescanMapping(register.id, item.name)
    }

    async function intelligent() {
        if (isManualMapTab) return
        if (!(await confirmScheduleMappingRemoval())) return
        setChosenSchedule(register.id, item.name)
        recordSelection({ register, entry: item, cleared: true })
        MappingUpdated.raiseOnce()
    }

    async function chooseSchedules() {
        selectItems(() => manuallySelectedSchedulesCombined, MULTI_SELECTION_REFERENCE_TYPE)
        const manuallySelected = await schedulesFromDialog()
        let manuallySelectedList = emptyArray
        if (Array.isArray(manuallySelected)) {
            manuallySelectedList = manuallySelected
        } else if (manuallySelected) {
            manuallySelectedList = [manuallySelected]
        }
        const manuallySelectedIds = Array.from(
            new Set(manuallySelectedList.map((s) => s?._id ?? s?.id).filter(Boolean))
        )
        if (manuallySelectedIds[0]) {
            try {
                await setManuallyChosenSchedules(register.id, item.name, manuallySelectedIds)
                item.schedule_ids_manual = manuallySelectedIds
                item.doNotMap = "create"
                successSnackbar(getMappedSchedulesSuccessMessage(manuallySelectedIds, item.name))
                recordSelection({ register, entry: item })
                onChosen()
                MappingUpdated.raiseOnce()
            } catch (e) {
                console.error(e)
                errorSnackbar("Failed to save selected schedules")
            }
        }
    }

    async function change() {
        if (!(await confirmScheduleMappingRemoval())) return
        item.schedules.forEach((c) => (c.chosen = false))
        if (item.doNotMap === "nothing") {
            setChosenSchedule(register.id, item.name, "").catch(console.error)
            onChosen()
            item.doNotMap = null
            MappingUpdated.raiseOnce()
        } else {
            setChosenSchedule(register.id, item.name, "nothing").catch(console.error)
            onChosen()
            item.doNotMap = "nothing"
            MappingUpdated.raiseOnce()
        }
        recordSelection({ register, entry: item })
    }

    function toggle() {
        setOpen((open) => !open)
    }

    function getToggleAction() {
        if (open) {
            return {
                actions: "Hide",
                icon: "mdi:arrow-down-drop",
                dataCy: "asset-register-map-types-mappings-expanded",
            }
        }
        return { actions: "Show", icon: "mdi:menu-right", dataCy: "asset-register-map-types-mappings-right" }
    }

    function expandDetailsRow() {
        setOpen(true)
    }

    function removeManuallySelectedSchedule(scheduleId) {
        removeQueueRef.current = removeQueueRef.current
            .catch(noop)
            .then(() => removeManuallySelectedScheduleInternal(scheduleId))
        return removeQueueRef.current
    }

    async function removeManuallySelectedScheduleInternal(scheduleId) {
        try {
            const scheduleName = getScheduleDisplayName(scheduleId)
            const removeChosenSchedule = scheduleId === item.schedule_id
            const currentSelectionManualIds = Array.isArray(item?.schedule_ids_manual)
                ? item.schedule_ids_manual
                : emptyArray
            const newSelectionManualIds = currentSelectionManualIds.filter((id) => id !== scheduleId)
            const nextScheduleId = removeChosenSchedule ? null : undefined

            await setManuallyChosenSchedules(register.id, item.name, newSelectionManualIds, nextScheduleId)
            if (removeChosenSchedule) {
                item.schedule_id = null
            }
            item.schedule_ids_manual = newSelectionManualIds
            successSnackbar(`Removed "${scheduleName}" from "${item.name}"`)
            recordSelection({ register, entry: item })
            MappingUpdated.raiseOnce()
        } catch (e) {
            console.error(e)
            errorSnackbar("Failed to remove selected schedule")
        }
    }

    async function reset() {
        try {
            const confirmRemove = await deleteAllSelectionsDialog()
            if (confirmRemove) {
                const nextScheduleId = item?.schedule_id ? null : undefined

                await setManuallyChosenSchedules(register.id, item.name, [], nextScheduleId)
                item.customSchedule = null
                item.schedule_id = null
                item.schedule_ids_manual = emptyArray
                successSnackbar(`Cleared selected schedules for "${item.name}"`)
                recordSelection({ register, entry: item, cleared: true })
                MappingUpdated.raiseOnce()
            }
        } catch (e) {
            console.error(e)
            errorSnackbar("Failed to clear selected schedules")
        }
    }

    function getScheduleDisplayName(scheduleId) {
        const treeIndex = getTreeIndex()
        const schedule = treeIndex.retrieve(scheduleId) ?? treeIndex.retrieve(`${scheduleId}:sfg20/schedule`)
        if (schedule?.label && schedule?.code) {
            return `${schedule.label} #${schedule.code}`
        }
        if (schedule?.label) {
            return schedule.label
        }
        return scheduleId
    }

    function getMappedSchedulesSuccessMessage(scheduleIds, assetName) {
        if (scheduleIds.length === 1) {
            return `Mapped "${getScheduleDisplayName(scheduleIds[0])}" to "${assetName}"`
        }
        return `${scheduleIds.length} schedules mapped to "${assetName}"`
    }

    async function create() {
        if (!(await confirmScheduleMappingRemoval())) return
        item.schedules.forEach((c) => (c.chosen = false))
        if (item.doNotMap === "create" && !isManualMapTab) {
            setChosenSchedule(register.id, item.name, "").catch(console.error)
            onChosen()
            item.doNotMap = null
            MappingUpdated.raiseOnce()
        } else {
            setChosenSchedule(register.id, item.name, "create").catch(console.error)
            onChosen()
            item.doNotMap = "create"
            MappingUpdated.raiseOnce()
        }
        if (item.doNotMap === "create" && !item.schedule_ids_manual?.length && !item.schedule_id) return
        recordSelection({ register, entry: item })
    }

    async function confirmScheduleMappingRemoval() {
        if (!isMapped) return true
        return confirm(
            "Changing this option will remove the current schedule mapping. Do you want to continue?",
            "Change Mapping Option"
        )
    }
}
